import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subscription, from, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onIdTokenChanged,
  Auth,
  User
} from 'firebase/auth';

import { environment } from '../../../environments/environment';

export type LoginResult = { ok: true; token: string } | { ok: false; error: { message: string } };

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private app: FirebaseApp;
  private auth: Auth;
  private refreshTimerSub: Subscription | null = null;

  private userSubject = new BehaviorSubject<User | null>(null);
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private authReadySubject = new BehaviorSubject<boolean>(false);

  user$ = this.userSubject.asObservable();
  token$ = this.tokenSubject.asObservable();
  authReady$ = this.authReadySubject.asObservable();

  constructor(private ngZone: NgZone) {
    this.app = initializeApp(environment.firebaseConfig);
    this.auth = getAuth(this.app);
    if (environment.firebaseTenantId) {
      this.auth.tenantId = environment.firebaseTenantId;
    }

    // onIdTokenChanged fires on sign-in/sign-out AND on silent token refresh,
    // so it also picks up a session already established by the sibling admin app
    // sharing this origin's IndexedDB.
    onIdTokenChanged(this.auth, (user: User | null) => {
      this.ngZone.run(async () => {
        this.userSubject.next(user);
        if (user) {
          const token = await user.getIdToken();
          this.tokenSubject.next(token);
          this.scheduleTokenRefresh(token);
        } else {
          this.tokenSubject.next(null);
          this.clearRefreshTimer();
        }
        if (!this.authReadySubject.value) {
          this.authReadySubject.next(true);
        }
      });
    });
  }

  private scheduleTokenRefresh(token: string): void {
    this.clearRefreshTimer();
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const delay = Math.max(expiresAt - Date.now() - 5 * 60 * 1000, 0);
      this.refreshTimerSub = timer(delay)
        .pipe(switchMap(() => from(this.getIdToken(true))))
        .subscribe();
    } catch {
      // Token couldn't be decoded; skip scheduling a refresh.
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerSub) {
      this.refreshTimerSub.unsubscribe();
      this.refreshTimerSub = null;
    }
  }

  signIn(email: string, password: string): Promise<string> {
    return signInWithEmailAndPassword(this.auth, email, password).then((credential) =>
      credential.user.getIdToken()
    );
  }

  /**
   * Convenience wrapper around signIn() that never throws: it resolves to a
   * result object carrying either the token or a user-facing error message
   * (translated via getFirebaseAuthErrorMessage), so callers can branch on
   * `ok` the same way the old app's login$ effect branched on the action type.
   */
  async login(email: string, password: string): Promise<LoginResult> {
    try {
      const token = await this.signIn(email, password);
      return { ok: true, token };
    } catch (error) {
      return { ok: false, error: { message: getFirebaseAuthErrorMessage(error) } };
    }
  }

  signOut(): Promise<void> {
    return signOut(this.auth);
  }

  getIdToken(forceRefresh = false): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return Promise.resolve(null);
    return user.getIdToken(forceRefresh);
  }

  get currentUser(): User | null {
    return this.auth.currentUser;
  }

  /**
   * Resolves once the first onIdTokenChanged callback has fired, i.e. once
   * Firebase has determined whether a session already exists (possibly one
   * established by the sibling admin app on the same origin).
   */
  authReady(): Promise<string | null> {
    return new Promise((resolve) => {
      const unsubscribe = onIdTokenChanged(this.auth, async (user) => {
        unsubscribe();
        if (user) {
          const token = await user.getIdToken();
          resolve(token);
        } else {
          resolve(null);
        }
      });
    });
  }
}

export function getFirebaseAuthErrorMessage(error: any): string {
  switch (error?.code) {
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    case 'auth/invalid-tenant-id':
      return 'Authentication configuration error. Please contact support.';
    case 'auth/password-does-not-meet-requirements':
      return 'Your current password doesn’t meet our security requirements. Please reset your password to continue.';
    case 'auth/internal-error':
      return extractBlockingFnMessage(error?.message) || 'Login failed. Please try again.';
    default:
      return extractBlockingFnMessage(error?.message) || error?.message || 'Login failed. Please try again.';
  }
}

/**
 * A Firebase Identity Platform "beforeSignIn" blocking function (used for the
 * OTP-required and password-policy checks) reports failures by embedding a
 * JSON payload inside the thrown auth/internal-error's message string. This
 * pulls that payload's message back out, appending a formatted retry delay
 * when the backend included one.
 */
function extractBlockingFnMessage(raw: any): string | null {
  if (typeof raw !== 'string') return null;
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[0]);
    const err = parsed?.error ?? parsed;
    const msg = err?.message;
    if (typeof msg !== 'string' || !msg.trim()) return null;
    const retryAfterSeconds = Number(err?.retryAfterSeconds);
    if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
      const base = msg
        .trim()
        .replace(/\s*Please try again later\.?\s*$/i, '')
        .replace(/\.?\s*$/, '');
      return `${base}. Try again in ${formatDuration(retryAfterSeconds)}.`;
    }
    return msg.trim();
  } catch {
    return null;
  }
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}
